import { useEffect, useState } from "react";

import { showAPIErrorMessage } from "utilities/apiHandler";

import {
    type ISelectionListContainerHandler,
    SelectionListContainerHandler,
} from "entity/selection/SelectionListContainer/SelectionListContainer.api.handler";
import { type SelectionConfig } from "entity/selection/SelectionListContainer/SelectionListContainer.types";

export interface IUseSelectionConfigResult {
    /** The job's config once loaded, or undefined while loading, on error, or when no valid job is selected */
    config: SelectionConfig | undefined;
    /** True only while a fetch is in flight for the current valid job. False once settled (success or error) or when no valid job */
    isLoading: boolean;
}

/**
 * Loads per-job Selections config keyed to its jobId, so a job switch invalidates the previous job's
 * config synchronously in render — closing the stale-config race that fired the wrong list query.
 * On error, config stays undefined and isLoading returns to false; callers treat that as legacy/fallback
 *
 * @param jobId Selected job, or null when there's no valid job (sentinels / empty selection)
 * @param handler Injectable config handler; defaults to the real SelectionListContainerHandler
 */
export function useSelectionConfig(
    jobId: number | null,
    handler?: ISelectionListContainerHandler,
): IUseSelectionConfigResult {
    // config === undefined on a settled entry marks an errored jobId (fall back), vs no entry (still loading)
    const [loaded, setLoaded] = useState<
        { jobId: number; config: SelectionConfig | undefined } | undefined
    >(undefined);

    const hasValidJob = jobId != null && jobId > 0;

    useEffect(() => {
        if (!hasValidJob) {
            return;
        }

        let cancelled = false;
        const loadConfig = async () => {
            try {
                const configHandler = handler ?? new SelectionListContainerHandler();
                const config = await configHandler.getConfig(jobId);
                if (!cancelled) {
                    setLoaded({ jobId, config });
                }
            } catch (e) {
                // Surface error, settle with no config so callers fall back to legacy instead of hanging
                if (!cancelled) {
                    showAPIErrorMessage(e);
                    setLoaded({ jobId, config: undefined });
                }
            }
        };

        void loadConfig();

        return () => {
            cancelled = true;
        };
    }, [hasValidJob, jobId, handler]);

    // Only trust a result matching the current job; a stale one would route to the wrong endpoint
    const settled = hasValidJob && loaded?.jobId === jobId;
    const config = settled ? loaded?.config : undefined;
    const isLoading = hasValidJob && !settled;

    return { config, isLoading };
}
